import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { belegLayout, type BelegArt } from "@talentkontor/domain";
import { useAction } from "../../../components/ui";
import { listeVorlagen, vorlageAnlegen, vorlageLoeschen, vorlageSpeichern, type BelegVorlage } from "../../../model/beleg-vorlagen";
import { BELEG_ARTEN } from "./PdfVorlagenEditor";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { date } from "../../../lib/format";
import { nameIn, sprachName, t, ta } from "../../../lib/sprache";
import { loadVorlagen, type KindRow, type TemplateRow } from "./data";
import { Confirm, NurAdmin, SettingsFrame } from "./parts";
import { EINST } from "./baum";

/**
 * PDF-Vorlagen (IA-1 Schritt 3, Zoho-Vorbild): alle Belege, die als PDF hinausgehen, in einer Liste, je mit dem Ort, der ihr
 * Aussehen und ihre Texte bestimmt; darunter die Vertragsvorlagen (gepflegt im Bereich Verträge). Nur lesend und verlinkend:
 * Rechnungen, Gutschriften, Mahnschreiben und Angebote haben ein festes Layout, das Branding, Firmendaten und Texte füllen.
 */
export function PdfVorlagen() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return <PdfVorlagenSeite orgId={ws.org.org_id} creator={!!ws.isCreator} timeZone={ws.org.timezone} />;
}

interface Beleg { key: string; name: string; inhalt: string; links: { to: string; label: string }[] }

function belege(creator: boolean): Beleg[] {
  const branding = { to: `${EINST}/branding`, label: t("Branding", "Branding") };
  const firma = { to: `${EINST}/firma`, label: t("Profil & Rechtsangaben", "Profile & legal details") };
  const nummern = { to: `${EINST}/nummernkreise`, label: t("Nummernkreise", "Number ranges") };
  const texte = { to: `${EINST}/vorlagen`, label: t("Mailvorlagen", "Email templates") };
  return [
    {
      key: "rechnung", name: t("Rechnung", "Invoice"),
      inhalt: t("Logo und Akzentfarbe, Firmen-, Steuer- und Bankangaben, Nummer nach dem Nummernkreis, Hinweistexte.", "Logo and accent colour, company, tax and bank details, number from the number range, note texts."),
      links: [branding, firma, nummern, texte],
    },
    ...(creator ? [] : [{
      key: "gutschrift", name: t("Gutschrift", "Self-billing invoice"),
      inhalt: t("Wie die Rechnung, mit eigenem Nummernkreis; auf Wunsch zusätzlich als E-Rechnung.", "Like the invoice, with its own number range; optionally also as an e-invoice."),
      links: [branding, nummern, { to: `${EINST}/gutschriften`, label: t("Gutschriften", "Self-billing") }],
    }]),
    {
      key: "mahnung", name: t("Zahlungserinnerung und Mahnung", "Payment reminder and dunning letter"),
      inhalt: t("Stufen, Fristen und Gebühren aus dem Mahnwesen, Texte aus den Mailvorlagen.", "Levels, deadlines and fees from dunning, texts from the email templates."),
      links: [{ to: `${EINST}/mahnleiter`, label: t("Mahnwesen", "Dunning") }, texte],
    },
    {
      key: "angebot", name: t("Angebot", "Offer"),
      inhalt: t("Einleitung, Bedingungen und Gültigkeit aus den Einstellungen der Anfragen, Nummer nach dem Nummernkreis.", "Introduction, terms and validity from the inquiry settings, number from the number range."),
      links: [{ to: `${EINST}/anfragen`, label: t("Anfragen & Angebote", "Inquiries & offers") }, nummern],
    },
    {
      key: "vertrag", name: t("Vertrag", "Contract"),
      inhalt: t("Text aus der Vertragsvorlage, Vertragsparteien aus den Firmendaten.", "Text from the contract template, contracting parties from the company details."),
      links: [{ to: "#vertrag", label: t("Vertragsvorlagen", "Contract templates") }, firma],
    },
  ];
}

function PdfVorlagenSeite({ orgId, creator, timeZone }: { orgId: string; creator: boolean; timeZone: string }) {
  // Gleiche Abfrage wie die Mailvorlagen: Vertragsvorlagen und Vertragsarten kommen von dort
  const q = useQuery({ queryKey: ["org_texts", orgId, "vorlagen"], queryFn: () => loadVorlagen(api, orgId) });
  const docLang = q.data ? (q.data.settings.default_document_language ?? q.data.settings.locale.slice(0, 2)) : "de";
  const vorlagen = useMemo(() => [...(q.data?.templates ?? [])].sort((a, b) => Number(b.language === docLang) - Number(a.language === docLang) || a.name.localeCompare(b.name, "de")), [q.data, docLang]);
  return (
    <SettingsFrame section="vorlagen" seite="pdf-vorlagen" orgId={orgId} title={t("PDF-Vorlagen", "PDF templates")}
      text={t("Wie Rechnungen, Mahnschreiben, Angebote und Verträge als PDF aussehen: Design, Farbe, Logo, Spalten, Texte. Je Marke lässt sich eine eigene Vorlage wählen.", "How invoices, dunning letters, offers and contracts look as PDF: design, colour, logo, columns, texts. Each brand can use its own template.")}>
      <BelegVorlagen orgId={orgId} creator={creator} />
      <section className="panel stack" id="vertrag" aria-labelledby="vertrag-title">
        <div className="row between">
          <h2 id="vertrag-title">{t("Vertragsvorlagen", "Contract templates")}</h2>
          <Link className="btn" to="/agentur/vertraege/vorlagen">{t("Alle Vorlagen im Bereich Verträge", "All templates in Contracts")}</Link>
        </div>
        {q.error ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Loading /> : vorlagen.length === 0 ? (
          <p>{t("Noch keine Vertragsvorlage. Im Bereich Verträge übernimmst du eine Vorlage von CreatorOffice oder legst eine eigene an.", "No contract template yet. In Contracts you adopt a CreatorOffice template or create your own.")}</p>
        ) : (
          <VertragsvorlagenTabelle vorlagen={vorlagen} kinds={q.data.kinds} timeZone={timeZone} />
        )}
      </section>
    </SettingsFrame>
  );
}

function VertragsvorlagenTabelle({ vorlagen, kinds, timeZone }: { vorlagen: TemplateRow[]; kinds: KindRow[]; timeZone: string }) {
  const status = (s: string) => (s === "active" ? <span className="pill success"><span className="pill-zeichen">✓</span> {t("Aktiv", "Active")}</span> : s === "draft" ? <span className="pill"><span className="pill-zeichen">○</span> {t("Entwurf", "Draft")}</span> : <span className="pill"><span className="pill-zeichen">○</span> {t("Archiviert", "Archived")}</span>);
  return (
    <div className="tablewrap tk-cardtable tk-vertragsvorlagen">
      <table>
        <caption className="sr-only">{ta("Vertragsvorlagen der Agentur", "Contract templates of the agency", "Deine Vertragsvorlagen", "Your contract templates")}</caption>
        <thead><tr><th scope="col">{t("Name", "Name")}</th><th scope="col">{t("Art", "Type")}</th><th scope="col">{t("Sprache", "Language")}</th><th scope="col">{t("Stand", "Status")}</th><th scope="col"><span className="sr-only">{t("Aktion", "Action")}</span></th></tr></thead>
        <tbody>
          {vorlagen.map((v) => {
            const kind = kinds.find((k) => k.id === v.kind_id);
            return (
              <tr key={v.id}>
                <td data-label={t("Name", "Name")} className="tk-titel-zelle"><strong>{v.name}</strong><small>{t(`Version ${v.version} · geändert ${date(v.updated_at, timeZone)}`, `Version ${v.version} · changed ${date(v.updated_at, timeZone)}`)}</small></td>
                <td data-label={t("Art", "Type")}>{kind ? nameIn(kind.name_de, kind.name_en) : "–"}</td>
                <td data-label={t("Sprache", "Language")}>{sprachName(v.language)}</td>
                <td data-label={t("Stand", "Status")}>{status(v.status)}</td>
                <td data-label={t("Aktion", "Action")}><Link className="btn" to={`/agentur/vertraege/vorlagen/${v.id}`}>{t("Bearbeiten", "Edit")}</Link></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Vorlagen je Belegart (0456): Liste mit Standard, Bearbeiten, als Standard setzen, duplizieren, löschen; darunter, woher die Inhalte kommen */
function BelegVorlagen({ orgId, creator }: { orgId: string; creator: boolean }) {
  const q = useQuery({ queryKey: ["document_templates", orgId], queryFn: () => listeVorlagen(orgId) });
  const qc = useQueryClient();
  const { busy, run } = useAction();
  const [loeschen, setLoeschen] = useState<BelegVorlage | null>(null);
  const neu = () => void qc.invalidateQueries({ queryKey: ["document_templates", orgId] });
  const [params] = useSearchParams();
  const ziel = params.get("art");
  // Sprung aus „PDF-Design“ (?art=invoice usw.) zur passenden Belegart, sobald die Karten stehen
  useEffect(() => {
    if (!ziel || q.isPending) return;
    document.getElementById(`pdf-${ziel}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [ziel, q.isPending]);
  const herkunft = belege(creator);
  const herkunftFuer: Record<BelegArt, string[]> = { invoice: ["rechnung", "gutschrift"], dunning: ["mahnung"], offer: ["angebot"], contract: ["vertrag"] };
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  return (
    <>
      {BELEG_ARTEN.map(({ art, name, text }) => {
        const liste = (q.data ?? []).filter((v) => v.kind === art);
        const links = herkunft.filter((b) => herkunftFuer[art].includes(b.key)).flatMap((b) => b.links).filter((l, i, all) => all.findIndex((x) => x.to === l.to) === i);
        return (
          <section key={art} id={`pdf-${art}`} className={`panel stack tk-pdf-art${ziel === art ? " ist-ziel" : ""}`} aria-labelledby={`pdf-${art}-titel`}>
            <div className="row between">
              <div className="stack tk-gap-sm">
                <h2 id={`pdf-${art}-titel`}>{name()}</h2>
                <small className="muted">{text()}</small>
              </div>
              <Link className="btn" to={`${EINST}/pdf-vorlagen/neu?art=${art}`}>{t("Neue Vorlage", "New template")}</Link>
            </div>
            {q.isPending ? <Loading /> : liste.length === 0 ? (
              <p className="muted">{t("Noch keine eigene Vorlage: Es gilt das Standard-Layout von CreatorOffice.", "No own template yet: the CreatorOffice standard layout applies.")}</p>
            ) : (
              <ul className="tk-pdf-liste">
                {liste.map((v) => (
                  <li key={v.id}>
                    <span className="tk-pdf-name"><strong>{v.name}</strong>{v.is_default && <span className="pill success"><span className="pill-zeichen">✓</span> {t("Standard", "Default")}</span>}</span>
                    <span className="row tk-pdf-aktionen">
                      <Link className="btn" to={`${EINST}/pdf-vorlagen/${v.id}`} aria-label={t(`${v.name} bearbeiten`, `Edit ${v.name}`)}>{t("Bearbeiten", "Edit")}</Link>
                      {!v.is_default && <button type="button" disabled={!!busy} onClick={() => void run(`std-${v.id}`, async () => { await vorlageSpeichern(v.id, { is_default: true }); neu(); return true; }, t("Als Standard gesetzt.", "Set as default."))}>{t("Als Standard", "Make default")}</button>}
                      <button type="button" disabled={!!busy} onClick={() => void run(`dup-${v.id}`, async () => { await vorlageAnlegen(orgId, v.kind, t(`${v.name} (Kopie)`, `${v.name} (copy)`).slice(0, 80), belegLayout(v.settings), false); neu(); return true; }, t("Kopie angelegt.", "Copy created."))}>{t("Duplizieren", "Duplicate")}</button>
                      <button type="button" disabled={!!busy} onClick={() => setLoeschen(v)} aria-label={t(`${v.name} löschen`, `Delete ${v.name}`)}>{t("Löschen", "Delete")}</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {links.length > 0 && (
              <small className="tk-pdf-herkunft">{t("Inhalte wie Firmendaten, Nummern und Texte kommen aus: ", "Contents such as company details, numbers and texts come from: ")}
                {links.map((l, i) => <span key={l.to}>{i > 0 ? " · " : ""}{l.to.startsWith("#") ? <a href={l.to}>{l.label}</a> : <Link to={l.to}>{l.label}</Link>}</span>)}</small>
            )}
          </section>
        );
      })}
      <Confirm open={!!loeschen} title={t(`„${loeschen?.name ?? ""}“ löschen?`, `Delete “${loeschen?.name ?? ""}”?`)} busy={!!busy} danger confirmLabel={t("Löschen", "Delete")}
        onClose={() => setLoeschen(null)}
        onConfirm={() => void run("loeschen", async () => { if (loeschen) await vorlageLoeschen(loeschen.id); setLoeschen(null); neu(); return true; }, t("Vorlage gelöscht.", "Template deleted."))}
        text={loeschen?.is_default
          ? t("Das ist die Standardvorlage. Danach gilt wieder das Standard-Layout von CreatorOffice, bis du eine andere Vorlage als Standard setzt. Marken mit dieser Vorlage nutzen dann den Standard.", "This is the default template. Afterwards the CreatorOffice standard layout applies until you set another default. Brands with this template then use the default.")
          : t("Marken mit dieser Vorlage nutzen danach den Standard. Bereits erstellte PDFs bleiben unverändert.", "Brands with this template then use the default. PDFs already created stay unchanged.")} />
    </>
  );
}
